/** A `table` field's value (SA-449, spec §8): a list of rows keyed by column
 * id. Pure helpers shared by the editor and the read-only rendering. */
import { isPlace, placeWords } from '@/components/records/place'
import { isBlank, sameValue } from './fieldState'

export type TableRow = Record<string, unknown>

/** The rows of a stored value; anything that is not a list of objects is no
 * rows (a record pinned to an older version never reaches a table field). */
export function rowsOf(value: unknown): TableRow[] {
  if (!Array.isArray(value)) return []
  return value.filter((row): row is TableRow => row !== null && typeof row === 'object' && !Array.isArray(row))
}

/** One cell as text: a blank is "—", an enum value reads with spaces. */
export function formatCell(value: unknown, type: string): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (isPlace(value)) return placeWords(value)
  if (type === 'enum' && typeof value === 'string') return value.replace(/_/g, ' ')
  return String(value)
}

/** The shape every column reader needs (a `TaskColumnRead` or a `TableColumnLike`). */
export interface ColumnLike {
  id: string
  type: string
  label?: string
  reference?: string | null
}

export type CellChange = 'none' | 'same' | 'changed'

/** The ids named as some column's reference (SA-1196, spec §2.3). */
export function referenceColumns(columns: readonly ColumnLike[]): Set<string> {
  return new Set(columns.flatMap((c) => (c.reference ? [c.reference] : [])))
}

/** The columns an editor or a read-only table draws: every one but the references, in order. */
export function visibleColumns<C extends ColumnLike>(columns: readonly C[]): C[] {
  const refs = referenceColumns(columns)
  return columns.filter((c) => !refs.has(c.id))
}

/** `changed` when the cell's value differs from its reference (a cleared cell
 * included), `same` when equal, `none` without a reference, with both blank,
 * or on an added row (spec §5.6): a row the pre-fill never had carries its
 * "new" mark and no per-cell one. `columns` are the field's declared columns,
 * the set `rowAdded` reads. One pure rule, which SA-1197 twins in Python. */
export function cellChange(row: TableRow, column: ColumnLike, columns: readonly ColumnLike[]): CellChange {
  if (!column.reference) return 'none'
  if (rowAdded(row, columns)) return 'none'
  const value = row[column.id], reference = row[column.reference]
  if (isBlank(value) && isBlank(reference)) return 'none'
  return sameValue(value, reference) ? 'same' : 'changed'
}

/** A row the pre-fill never had: every reference cell blank while some referencing cell is not. */
export function rowAdded(row: TableRow, columns: readonly ColumnLike[]): boolean {
  const referencing = columns.filter((c) => c.reference)
  if (referencing.length === 0) return false
  return referencing.every((c) => isBlank(row[c.reference as string])) && referencing.some((c) => !isBlank(row[c.id]))
}
